import { FiTool, FiCheckCircle } from 'react-icons/fi';
import { formatDisplayShort } from '../../../lib/dateUtils';
import { isAdmin } from '../../../lib/roles';
import { ELEMENT_TYPE_OPTIONS } from '../../../constants/AssetTestConfig';
import Button from '../../../components/ui/Button';
import Badge from '../../../components/ui/Badge';
import { RepairCompleteModal } from './RepairCompleteModal';
import RepairSessionFinishModal from './RepairSessionFinishModal';
import { useRepairSession } from '../hooks/useRepairSession';

function confirmFinishRepairSession(user) {
  if (isAdmin(user)) return true;
  const name = String(user?.firstName || '').trim().toUpperCase();
  const prefix = name ? `${name} ` : '';
  return window.confirm(
    `${prefix}are you sure you have completed all the repairs for this site??? only click finish repair session if all the repairs are done`
  );
}

function formatAssetTypeLabel(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return 'Unknown type';
  return raw
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function isWorkshopEquipmentDefect(defect) {
  return (defect?.asset_type || '').trim().toLowerCase() === 'workshop_equipment';
}

/**
 * Dedicated repair session view — completely independent of TestSessionView.
 *
 * Shows a flat list of open defects (all fail/needs_repair tests that
 * have not yet been resolved by a repair_record). Each row has a single action:
 * "Mark Repaired" which opens RepairCompleteModal (before + after photos; repaired vs replaced).
 *
 * No group panes, no 90-min timer, no pass/fail result selector.
 *
 * Route entry: /repairs  (optionally with location.state.prefillSiteId)
 */
export function RepairSessionView() {
  const {
    user,
    elementTypeFilter,
    setElementTypeFilter,
    workOrderNumber,
    setWorkOrderNumber,
    search,
    setSearch,
    isLoading,
    error,
    refetch,
    defects,
    grouped,
    totalOpen,
    sessionRepaired,
    sessionRepairedCount,
    activeDefect,
    setActiveDefect,
    activeRepairRecord,
    setActiveRepairRecord,
    showFinishModal,
    setShowFinishModal,
    isFinishing,
    handleRepaired,
    handleBeforePhotoSaved,
    performFinishSession,
  } = useRepairSession();

  return (
    <div className="repair-session-view">
      {/* Header */}
      <div className="repair-session-header">
        <div className="repair-session-title-row">
          <FiTool size={20} aria-hidden />
          <h1 className="repair-session-title">Repair Session</h1>
        </div>

        {sessionRepairedCount > 0 && (
          <div className="repair-session-header-actions">
            <div className="repair-session-progress">
              <FiCheckCircle size={16} aria-hidden />
              <span>{sessionRepairedCount} repaired this session</span>
            </div>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                if (!confirmFinishRepairSession(user)) return;
                setShowFinishModal(true);
              }}
            >
              Finish repair session
            </Button>
          </div>
        )}
      </div>

      {/* Toolbar */}
      <div className="repair-session-toolbar">
        <input
          type="text"
          className="repair-session-work-order"
          placeholder="Work Order #"
          value={workOrderNumber}
          onChange={(e) => setWorkOrderNumber(e.target.value)}
          aria-label="Repair work order number"
        />
        <select
          className="repair-session-element-type"
          value={elementTypeFilter}
          onChange={(e) => setElementTypeFilter(e.target.value)}
          aria-label="Filter defects by element type"
        >
          <option value="">All element types</option>
          {ELEMENT_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <input
          type="search"
          className="repair-session-search"
          placeholder="Search assets, location, defect…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search open defects"
        />
      </div>

      {/* Body */}
      {isLoading ? (
        <p className="text-muted repair-session-state">Loading open defects…</p>
      ) : error ? (
        <div className="repair-session-state">
          <p className="page-error">Failed to load open defects.</p>
          <Button type="button" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      ) : defects.length === 0 ? (
        <div className="repair-session-state">
          {totalOpen === 0 && sessionRepairedCount === 0 ? (
            <>
              <FiCheckCircle size={40} className="repair-session-empty-icon" aria-hidden />
              <p className="text-muted">No open defects — all clear!</p>
            </>
          ) : (
            <p className="text-muted">No defects match your search or element type filter.</p>
          )}
        </div>
      ) : (
        <div className="repair-session-groups">
          {grouped.map((group) => (
            <div key={`${group.building}|||${group.room}`} className="repair-group">
              <h3 className="repair-group-title">
                {group.building}
                {group.room && group.room !== 'Unspecified Room' && (
                  <span className="repair-group-room"> › {group.room}</span>
                )}
              </h3>

              <ul className="repair-group-list">
                {group.items.map((defect) => {
                  const repairedEntry = sessionRepaired.get(defect.test_result_id);
                  const isRepairedThisSession = !!repairedEntry;
                  return (
                    <li
                      key={defect.test_result_id}
                      className={`repair-defect-card${isRepairedThisSession ? ' repair-defect-card--repaired' : ''}`}
                    >
                      <div className="repair-defect-header">
                        <span className="repair-defect-name">
                          {defect.asset_name || defect.equipment_code || defect.asset_code || `Asset ${defect.asset_id}`}
                        </span>
                        <div className="repair-defect-header-badges">
                          <Badge variant="muted" className="repair-defect-type-badge" title="Asset type">
                            {formatAssetTypeLabel(defect.asset_type)}
                          </Badge>
                          {isRepairedThisSession ? (
                            <Badge variant="success">Repaired</Badge>
                          ) : (
                            <Badge variant="danger">Defect</Badge>
                          )}
                        </div>
                      </div>

                      {(defect.make || defect.model) && (
                        <p className="repair-defect-make-model text-muted">
                          {[defect.make, defect.model].filter(Boolean).join(' / ')}
                        </p>
                      )}

                      {isWorkshopEquipmentDefect(defect) && String(defect.item_type || '').trim() && (
                        <p className="repair-defect-item-type text-muted">
                          <span className="repair-defect-item-type-label">Item / Type</span>
                          {String(defect.item_type).trim()}
                        </p>
                      )}

                      {defect.failed_at && (
                        <div className="repair-defect-meta">
                          <span className="text-muted">
                            Failed {formatDisplayShort(defect.failed_at)}
                          </span>
                        </div>
                      )}

                      {defect.defects_found && (
                        <p className="repair-defect-description">{defect.defects_found}</p>
                      )}

                      <div className="repair-defect-actions">
                        {isRepairedThisSession ? (
                          <span className="text-muted repair-defect-recorded">Repair recorded — visible until you finish this session.</span>
                        ) : (
                          <>
                            {defect.in_progress_repair && (
                              <span className="text-muted repair-defect-in-progress">
                                Before photo saved — add the after photo to finish.
                              </span>
                            )}
                            <Button
                              type="button"
                              variant="primary"
                              className="repair-defect-action-btn"
                              onClick={() => {
                                setActiveRepairRecord(defect.in_progress_repair || null);
                                setActiveDefect(defect);
                              }}
                            >
                              {defect.in_progress_repair ? 'Finish Repair' : 'Mark Repaired'}
                            </Button>
                          </>
                        )}
                        {isRepairedThisSession && (
                          <Button
                            type="button"
                            className="repair-defect-action-btn"
                            onClick={() => {
                              setActiveRepairRecord(repairedEntry?.record || null);
                              setActiveDefect(defect);
                            }}
                          >
                            Edit
                          </Button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Repair completion modal */}
      {activeDefect && (
        <RepairCompleteModal
          defect={activeDefect}
          existingRecord={activeRepairRecord}
          workOrderNumber={workOrderNumber}
          onClose={() => {
            setActiveDefect(null);
            setActiveRepairRecord(null);
          }}
          onRepaired={handleRepaired}
          onBeforePhotoSaved={handleBeforePhotoSaved}
        />
      )}

      {showFinishModal && (
        <RepairSessionFinishModal
          repairedCount={sessionRepairedCount}
          onClose={() => setShowFinishModal(false)}
          onComplete={performFinishSession}
          isFinishing={isFinishing}
        />
      )}
    </div>
  );
}
